import React, { useState, useEffect } from "react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { listPricelists } from "../graphql/queries";
import { updateUser } from "../graphql/mutations";
import { Amplify, Auth } from "aws-amplify";
import { useUser } from "../UserContext";
import { Button, Box, Stack, Typography } from "@mui/material";

import PricelistSearch from "./search/SearchPricelist";
import { useTheme } from "@mui/material/styles";
import { useParams, useSearchParams, Link } from "react-router-dom";
import OurLoader from "./OurLoader";
import MetalPriceChart from "./MetalsChart";
import NoCodeSearch from "./search/NoCodeSearch";

const SearchPricelist = () => {
  const { userClass, userRecord, refreshUserAndPricelist } = useUser();
  const [csvData, setCsvData] = useState([]);
  const [searchedPart, setSearchedPart] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentPricelists, setRecentPricelists] = useState([]);
  const [selectedPricelist, setSelectedPricelist] = useState(null);

  const { defaultSearch } = useParams();
  const [searchParams] = useSearchParams();
  const defaultSearchMode = searchParams.get("defaultSearchMode");

  const [searchTerm, setSearchTerm] = useState(
    defaultSearch ? defaultSearch : "",
  );

  const [searchMode, setSearchMode] = useState(defaultSearchMode || "metals");

  const theme = useTheme();

  // Reset to metals tab if no specific mode is requested
  useEffect(() => {
    if (!defaultSearchMode) {
      setSearchMode("metals");
    } else {
      setSearchMode(defaultSearchMode);
    }
  }, [defaultSearchMode]);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((usr) => {
        setUser(usr);
        console.log("userClass", userClass);
        // If user is admin, fetch recent pricelists
        if (["Admin", "SuperAdmin"].includes(userClass)) {
          fetchRecentPricelists();
        }
      })
      .catch((err) => console.log(err));
  }, [userClass]);

  const fetchLatestCSV = async () => {
    console.log("fetchLatestCSV called");
    try {
      if (!userRecord) {
        console.log("No user record found, returning early");
        setLoading(false);
        return;
      }

      console.log("User record:", userRecord);
      console.log("Active pricelist ID:", userRecord.activePricelistID);

      if (!userRecord.activePricelistID) {
        console.log("No active pricelist ID found, returning early");
        setLoading(false);
        return;
      }

      // If we don't have the activePricelist object but we have the ID, fetch it
      if (!userRecord.activePricelist && userRecord.activePricelistID) {
        console.log("Fetching active pricelist details");
        try {
          const pricelistResponse = await API.graphql(
            graphqlOperation(listPricelists, {
              filter: { id: { eq: userRecord.activePricelistID } },
            }),
          );

          const pricelistItems = pricelistResponse.data.listPricelists.items;
          if (pricelistItems && pricelistItems.length > 0) {
            userRecord.activePricelist = pricelistItems[0];
            console.log(
              "Fetched active pricelist:",
              userRecord.activePricelist,
            );
          } else {
            console.log(
              "No pricelist found with ID:",
              userRecord.activePricelistID,
            );
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error("Error fetching pricelist details:", error);
          setLoading(false);
          return;
        }
      }

      if (!userRecord.activePricelist?.s3Key) {
        console.log("No active pricelist s3Key found, returning early");
        setLoading(false);
        return;
      }

      // Check if the uploadedAt date is the same in local storage
      const cachedDate = localStorage.getItem("uploadedAt") || null;
      const cachedPricelistId = localStorage.getItem("pricelistId") || null;
      console.log("Cached date:", cachedDate);
      console.log("Cached pricelist ID:", cachedPricelistId);
      console.log("Current pricelist ID:", userRecord.activePricelistID);
      console.log(
        "Active pricelist created at:",
        userRecord.activePricelist.createdAt,
      );

      let jsonData;
      // Only use cache if the pricelist ID matches and the date matches
      if (
        cachedDate &&
        cachedPricelistId === userRecord.activePricelistID &&
        new Date(cachedDate).getTime() ===
          new Date(userRecord.activePricelist.createdAt).getTime()
      ) {
        console.log("Cache hit - using cached data");
        // Use cached data
        jsonData = JSON.parse(localStorage.getItem("csvData"));
        console.log("Parsed cached data length:", jsonData?.length);
        if (jsonData && jsonData.length > 0) {
          console.log("Setting CSV data from cache");
          setCsvData(jsonData);
          setLoading(false);
        } else {
          console.log("Cached data was empty or invalid");
          setLoading(false);
        }
      } else {
        console.log("Cache miss - fetching from S3");
        // Fetch the file from S3
        const fileUrl = await Storage.get(userRecord.activePricelist.s3Key, {
          download: true,
        });

        if (fileUrl.Body) {
          console.log("Successfully got file from S3");
          // Parse the data as JSON
          const blob = new Blob([fileUrl.Body], { type: "application/json" });
          const jsonText = await blob.text();
          jsonData = JSON.parse(jsonText);
          console.log("Parsed S3 data length:", jsonData?.length);

          if (jsonData && jsonData.length > 0) {
            console.log("Caching valid data from S3");
            // Cache the data in local storage
            localStorage.setItem("csvData", JSON.stringify(jsonData));
            localStorage.setItem(
              "uploadedAt",
              userRecord.activePricelist.createdAt,
            );
            localStorage.setItem("pricelistId", userRecord.activePricelistID);
            setCsvData(jsonData);
            setLoading(false);
          } else {
            console.log("S3 data was empty or invalid");
            setLoading(false);
          }
        } else {
          console.log("No file body received from S3");
          setLoading(false);
        }
      }
    } catch (error) {
      console.error("Error fetching CSV data:", error);
      console.log("Error details:", {
        message: error.message,
        code: error.code,
        stack: error.stack,
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLatestCSV();
  }, [userRecord?.activePricelist]);

  useEffect(() => {
    console.log(
      "csvData before useEffect",
      JSON.parse(localStorage.getItem("csvData")),
    );
    if (localStorage.getItem("csvData")) {
      // console.log('csvData', localStorage.getItem("csvData"));
      setCsvData(JSON.parse(localStorage.getItem("csvData")));
    } else {
      console.log("no csvData in localStorage");
      fetchLatestCSV();
    }
  }, []);

  useEffect(() => {
    if (searchTerm) {
      let results = csvData.filter(
        (item) => item["PART NUMBER"] === searchTerm,
      );
      console.log("results", results);
      // Limit the results to the first 20 items

      results = results.slice(0, 20);

      let newPart = null;

      if (results.length > 0) {
        newPart = {
          price: results[0]["PRICES"],
          partNumber: results[0]["PART NUMBER"],
        };
      }
      setSearchedPart(newPart);
    } else {
      setSearchedPart(null);
    }
  }, [searchTerm]);

  const fetchRecentPricelists = async () => {
    try {
      const response = await API.graphql(
        graphqlOperation(listPricelists, { limit: 10 }),
      );
      const pricelists = response.data.listPricelists.items;
      console.log("pricelists", pricelists);
      setRecentPricelists(pricelists);
    } catch (error) {
      console.error("Error fetching recent pricelists:", error);
    }
  };

  const handlePricelistChange = async (event) => {
    const selectedId = event.target.value;
    const selected = recentPricelists.find((pl) => pl.id === selectedId);
    setSelectedPricelist(selected);

    if (selected) {
      try {
        setLoading(true);

        // Update the user's active pricelist
        await API.graphql(
          graphqlOperation(updateUser, {
            input: {
              id: userRecord.id,
              activePricelistID: selected.id,
            },
          }),
        );

        // Refresh the user context to get the updated activePricelist
        await refreshUserAndPricelist();

        // Load the new pricelist data
        const fileUrl = await Storage.get(selected.s3Key, { download: true });

        if (fileUrl.Body) {
          const blob = new Blob([fileUrl.Body], { type: "application/json" });
          const jsonText = await blob.text();
          const jsonData = JSON.parse(jsonText);

          if (jsonData && jsonData.length > 0) {
            setCsvData(jsonData);
          }
        }
      } catch (error) {
        console.error("Error updating active pricelist:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const buttonStyle = {
    backgroundColor: "transparent",
    color: theme.palette.text.primary,
    borderWidth: "2px",
    borderStyle: "solid",
    borderRadius: "4px",
    padding: "8px 16px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  };

  return (
    <Box
      className="page-container"
      sx={{
        backgroundColor: "#000000",
        minHeight: "100vh",
        color: theme.palette.text.primary,
      }}
    >
      <Stack
        style={{
          marginTop: ["Admin", "SuperAdmin"].includes(userClass)
            ? "80px"
            : "80px",
        }}
        direction="row"
        spacing={1}
        className="nav-buttons"
        sx={{ justifyContent: "center" }}
      >
        <Button
          style={{
            ...buttonStyle,
            borderColor:
              searchMode === "metals"
                ? theme.palette.primary.main
                : theme.palette.text.secondary,
          }}
          onClick={() => setSearchMode("metals")}
        >
          Charts & Notifications
        </Button>
        <Button
          style={{
            ...buttonStyle,
            borderColor:
              searchMode === "oem"
                ? theme.palette.primary.main
                : theme.palette.text.secondary,
          }}
          onClick={() => setSearchMode("oem")}
        >
          Search By Code
        </Button>
        <Button
          style={{
            ...buttonStyle,
            borderColor:
              searchMode === "nocode"
                ? theme.palette.primary.main
                : theme.palette.text.secondary,
          }}
          onClick={() => setSearchMode("nocode")}
        >
          No Code Search
        </Button>
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: searchMode === "metals" ? "block" : "none" }}>
          <MetalPriceChart metalSymbols={["PD", "PT", "RH"]} />
        </Box>
        <Box sx={{ display: searchMode === "oem" ? "block" : "none" }}>
          {loading ? (
            <OurLoader />
          ) : csvData.length === 0 ? (
            <Box textAlign="center" sx={{ mt: 4 }}>
              <Typography variant="h6">
                No Price Lists Have Been Uploaded.
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                Please{" "}
                <Link
                  to="/manage-pricelists"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: "underline",
                  }}
                >
                  upload or select a price list
                </Link>{" "}
                to continue.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.primary.main,
                  marginBottom: "20px",
                  textAlign: "center",
                  fontFamily: "Poppins",
                }}
              >
                {userRecord?.activePricelist ? (
                  <>
                    Current Price List:{" "}
                    {userRecord.activePricelist.name ||
                      `#${userRecord.activePricelist.id}`}
                    <br />
                    <span style={{ fontSize: "0.8rem" }}>
                      Uploaded on{" "}
                      {new Date(
                        userRecord.activePricelist.createdAt,
                      ).toLocaleDateString()}
                    </span>
                  </>
                ) : (
                  "No active pricelist selected"
                )}
              </Typography>
              <PricelistSearch
                defaultSearch={defaultSearch}
                csvData={csvData}
                setCsvData={setCsvData}
                recentPricelists={recentPricelists}
                selectedPricelist={selectedPricelist}
                handlePricelistChange={handlePricelistChange}
                userClass={userClass}
              />
            </>
          )}
        </Box>
        <Box sx={{ display: searchMode === "nocode" ? "block" : "none" }}>
          <NoCodeSearch />
        </Box>
      </Box>
    </Box>
  );
};

export default SearchPricelist;
